import React from 'react';
import _ from 'lodash';
import useToggle from 'react-use-v13/lib/useToggle';

import { getAnnotationColor, getAnnotationId } from '@scale/frontend-lib/annotation';

import { Annotation, AnnotationAttributes } from '@scale/shared/types/annotation';
import { BreakdownColors } from '@scale/shared/annotation';

import LinkedAnnotationRow from './LinkedAnnotationRow';
import LinkedAnnotationSelector from './LinkedAnnotationSelector';

/*
 * Used for linked attributes in 2D image annotation.
 * Note that semseg uses a different component, <LinkedLabelsPicker />
 */

interface LinkedAnnotationProps {
  isEditableMode: boolean;
  annotations: Annotation[];
  breakdownColors: BreakdownColors;
  selectedBreakdown: string;
  attrDefinitions: AnnotationAttributes;
  attrKey: string;
  annotationId: string;
  selectedChoice: string;
  handleAttributesChange: (attrKey: string, val?: string) => void;
  handleLinkedAttributeClick: (a: Annotation) => void;
}

const LinkedAnnotation: React.FC<LinkedAnnotationProps> = props => {
  const {
    isEditableMode,
    annotationId,
    annotations,
    breakdownColors,
    selectedBreakdown,
    attrDefinitions,
    attrKey,
    selectedChoice,
    handleAttributesChange,
    handleLinkedAttributeClick,
  } = props;

  const [isOpen, toggleOpen] = useToggle(false);

  const handleClose = () => {
    toggleOpen(false);
  };

  const handleOpen = () => {
    if (isEditableMode) {
      toggleOpen(true);
    }
  };

  const handleSelectLabel = (val?: string) => {
    handleAttributesChange(attrKey, val);
    handleClose();
  };

  const getColorForAnnotation = (annotation: Annotation) => {
    return getAnnotationColor(annotation, selectedBreakdown, breakdownColors, {
      defaultColor: '#333',
      attributeDefinitions: attrDefinitions,
    });
  };

  const attributeDefinition = {
    name: attrKey,
    definition: attrDefinitions[attrKey],
  };

  const selectedAnnotation = (() => {
    if (!selectedChoice || _.isEmpty(annotations)) {
      return undefined;
    }

    const annotation = annotations.find(item => getAnnotationId(item) === selectedChoice);

    if (!annotation) {
      return undefined;
    }

    return {
      ...annotation,
      color: getAnnotationColor(annotation, selectedBreakdown, breakdownColors),
    };
  })();

  if (attributeDefinition === undefined) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <LinkedAnnotationSelector
          annotationId={annotationId}
          annotations={annotations}
          getColorForAnnotation={getColorForAnnotation}
          attributeDefinition={attributeDefinition}
          selectedId={selectedChoice}
          handleSelectLabel={handleSelectLabel}
          handleClose={handleClose}
        />
      )}
      <LinkedAnnotationRow
        isEditableMode={isEditableMode && !_.isEmpty(annotations)}
        handleOpen={handleOpen}
        selectedAnnotation={selectedAnnotation}
        handleLinkedAttributeClick={handleLinkedAttributeClick}
      />
    </>
  );
};

export default LinkedAnnotation;
